import {IReglasCarga, aceptaArchivo, reglasDeCarga, textoFormatos} from "../../data/constants/carga-documento"
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as CapacitorCore from "@capacitor/core";
import * as CapacitorBrowser from "@capacitor/browser";
import {IFlow, IFlowFile} from "../../../../../data/interfaces/iflow.interface";
import {
  faCamera,
  faCar,
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleInfo,
  faCloudArrowUp,
  faDownload,
  faExclamation,
  faFileImage,
  faFileLines,
  faFilePdf,
  faFolderOpen,
  faIdCard,
  faLightbulb,
  faPassport,
  faPlus,
  faRightLeft,
  faRotateRight,
  faShieldHalved,
  faTrashCan,
  faXmark,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import {
  ApiFlowDataService,
  IRespuestaDocumento
} from "../../../../../data/services/api/flow/api-flow-data.service";
import {
  cantidadArchivos,
  etiquetaArchivo,
  minimoArchivos,
  TAMANO_MAXIMO_DOCUMENTO,
  TAMANO_MAXIMO_DOCUMENTO_TEXTO
} from "../../data/constants/documento-archivos.constant";
import {
  FORMATOS_DOCUMENTO_TEXTO,
  formatoFechaSubida,
  formatoTamano,
  tipoAceptado,
  tipoDeArchivo,
  TipoDocumento
} from "../../data/constants/documento-archivos.formato";
import {
  archivosDeIdentificacion,
  guiaDeIdentificacion,
  Identificacion,
  identificacionesDe,
  NOMBRE_IDENTIFICACION
} from "../../data/constants/identificaciones";
import {RecuadroCamara} from "../../data/constants/camara-guia";

export type EstadoArchivo = 'PENDIENTE' | 'CARGADO' | 'RECHAZADO'

// Lo que se subió en esta sesión (para la lista «Tus documentos»).
export interface IArchivoSubido {
  nombre: string
  tamano: number
  tipo: TipoDocumento
  miniatura: string | null
}

// Un archivo del paso: en multiarchivo uno por índice («Último», «Penúltimo»…);
// en el paso de un archivo, `unico`.
export interface IRenglonArchivo {
  index: number
  etiqueta: string
  estado: EstadoArchivo
  comentario: string
  archivo: File | null
  // Object URL del archivo elegido (solo imágenes); se revoca al quitarlo.
  vistaPrevia?: string | null
  errorSubida?: string
  subido?: IArchivoSubido | null
  fechaSubida?: Date | string | null
  url?: string
}

// Varios a la vez (multiarchivo): `listo` = elegido y por subir; el resto, su
// lugar en la cola de subida.
export type EstadoFilaLote = 'listo' | 'espera' | 'subiendo' | 'guardado' | 'error' | 'cancelado'

// Un archivo de la cola. Nombre, tamaño y tipo se guardan al formarla porque al
// guardarse el archivo elegido se libera (registrarSubida).
export interface IItemCola {
  index: number
  nombre: string
  tamano: number
  tipo: TipoDocumento
  estado: Exclude<EstadoFilaLote, 'listo'>
  error: string
}

// Un renglón del resumen «N archivos listos para subir» / de la cola.
export interface IFilaLote {
  index: number
  etiqueta: string
  nombre: string
  tamano: number
  tipo: TipoDocumento
  miniatura: string | null
  estado: EstadoFilaLote
  error: string
}

// Un archivo de una selección múltiple que no se pudo usar (tipo o tamaño).
export interface IArchivoDescartado {
  nombre: string
  motivo: string
}

// Una tarjeta de «¿Qué identificación vas a subir?».
export interface IOpcionIdentificacion {
  clave: Identificacion
  nombre: string
  // Qué archivos pide: «Anverso y reverso», «La hoja de datos, la de tu foto»…
  pide: string
  icono: IconDefinition
}

type EtapaRespuesta = (Partial<IFlow> & {_id?: string; completed?: boolean}) | null
type EstadoVisual = 'listo' | 'activo' | 'pendiente' | 'rechazado'
type ArchivosDelPaso = Pick<IFlow, 'fileCount' | 'fileCountMin' | 'fileLabels'>
type DestinoFoco = 'titulo' | 'accion' | 'identidad' | 'selector' | 'confirmar'

const ICONO_IDENTIFICACION: Record<Identificacion, IconDefinition> = {
  INE: faIdCard,
  PASAPORTE: faPassport,
  LICENCIA: faCar
}

// Lo que pide cada identificación (la INE, los archivos del documento).
const PIDE_IDENTIFICACION: Partial<Record<Identificacion, string>> = {
  PASAPORTE: 'La hoja de datos, la de tu foto',
  LICENCIA: 'Frente y reverso'
}

const renglonVacio = (index: number, etiqueta: string): IRenglonArchivo => ({
  index,
  etiqueta,
  estado: 'PENDIENTE',
  comentario: '',
  archivo: null,
  vistaPrevia: null,
  errorSubida: '',
  subido: null,
  fechaSubida: null,
  url: ''
})

const mismaFila = (a: IFilaLote, b: IFilaLote): boolean =>
  a.index === b.index && a.etiqueta === b.etiqueta && a.nombre === b.nombre && a.tamano === b.tamano &&
  a.tipo === b.tipo && a.miniatura === b.miniatura && a.estado === b.estado && a.error === b.error

// «a», «a y b», «a, b y c», «a, b, c y 2 más».
const unirNombres = (nombres: string[]): string => {
  if (nombres.length <= 1) return nombres[0] ?? ''
  if (nombres.length > 3) return `${nombres.slice(0, 3).join(', ')} y ${nombres.length - 3} más`
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`
}

// Tiempo que se queda la celebración antes de regresar solo a la lista.
const ESPERA_REGRESO = 4000

// «Anverso y reverso» con las etiquetas del documento; sin ellas, lo de una INE.
const textoPideIne = (archivos: ArchivosDelPaso): string => {
  const cantidad = cantidadArchivos(archivos)
  const etiquetas = (archivos.fileLabels ?? []).slice(0, cantidad).map(e => (e ?? '').trim())
  if (cantidad > 1 && etiquetas.length === cantidad && etiquetas.every(Boolean)) {
    return unirNombres(etiquetas.map((e, i) => (i ? e.charAt(0).toLowerCase() + e.slice(1) : e)))
  }
  if (cantidad === 1) return 'Una foto de tu credencial'
  return cantidad === 2 ? 'Anverso y reverso' : `${cantidad} archivos`
}

@Component({
  selector: 'app-document-component',
  templateUrl: './document-component.component.html',
  styleUrls: ['./document-component.component.scss']
})
export class DocumentComponentComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @Input() data!: IFlow
  @Input() pushingId: string = ''
  @Output() onEndProcess = new EventEmitter()
  // Avisa que el paso cambió sin completarse (un archivo de varios) para que la
  // lista se recargue por detrás; si no, al volver seguiría en «0 de N».
  @Output() onUpdateStage = new EventEmitter()
  @ViewChild('areaCarga') areaCarga?: ElementRef<HTMLElement>
  @ViewChild('refTitulo') tituloLoteRef?: ElementRef<HTMLElement>
  @ViewChild('refAccion') accionLoteRef?: ElementRef<HTMLElement>
  @ViewChild('refZona') botonZonaRef?: ElementRef<HTMLElement>
  @ViewChild('refReemplazo') inputReemplazoRef?: ElementRef<HTMLInputElement>
  @ViewChild('inputCamara') inputCamaraRef?: ElementRef<HTMLInputElement>
  @ViewChild('refIdentidad') identidadRef?: ElementRef<HTMLElement>
  @ViewChild('refSelector') selectorRef?: ElementRef<HTMLElement>
  @ViewChild('refConfirmar') confirmarRef?: ElementRef<HTMLElement>

  // Paso multiarchivo (fileCount > 1): cada archivo se sube por separado con su
  // fileIndex y el backend da el paso por completo hasta tener los N (o el
  // mínimo, si el paso trae rango: «de 4 a 6» pasa con 4).
  renglones: IRenglonArchivo[] = []
  // Paso de un archivo: el mismo modelo, fuera de la rejilla.
  unico: IRenglonArchivo = renglonVacio(0, '')
  indiceActivo = 0
  subiendo = false
  progreso = 0
  analizando = false
  arrastrando = false
  avisoValidacion = ''
  mensajeExito = ''
  terminado = false
  // Con rango: el paso ya pasó con el mínimo pero quedan opcionales. No se
  // celebra ni regresa solo; se queda con el aviso y «Continuar» por si quiere
  // subir los demás (el backend acepta subidas al paso ya VALIDATED).
  completoMinimo = false

  // Varios a la vez. La cola sube uno por uno con el mismo subirArchivo de
  // siempre; `cola` se queda como resumen al terminar hasta que cambie la selección.
  cola: IItemCola[] = []
  colaActiva = false
  cancelando = false
  // Renglón del resumen abierto para cambiar o quitar su archivo.
  edicion: number | null = null
  archivosDescartados: IArchivoDescartado[] = []
  avisoSobrantes: {titulo: string; texto: string} | null = null

  // Cómo se sube ESTE documento (Omnicanal: settings.captura / extensiones). Sin
  // configurar: foto o archivo, JPG/PNG/PDF, como siempre. Campos, no getters.
  reglasCarga: IReglasCarga = reglasDeCarga(null)
  acceptCamara = 'image/*'
  acceptArchivo = 'image/*,application/pdf'
  // Cámara con recuadro (settings.guia): abierta para elegir o para «Cambiar» un
  // renglón del resumen; null = cerrada. La foto sale recortada al recuadro.
  camaraGuia: {destino: 'elegir' | 'reemplazo'; etiqueta: string} | null = null
  tipoFotoCamara: 'image/jpeg' | 'image/png' = 'image/jpeg'

  // Documento de identidad (Omnicanal: settings.identificaciones). Las que acepta
  // (del paso; con un backend sin el campo, de settings) y la elegida: la que
  // guardó el servidor, la única aceptada o la que se elige aquí. Siempre va por
  // renglones con fileIndex, aunque la elegida pida un archivo (pasaporte).
  identificaciones: Identificacion[] = []
  identificacion: Identificacion | null = null
  opcionesIdentificacion: IOpcionIdentificacion[] = []
  // «Cambiar identificación»: el selector otra vez y, si ya hay archivos, a cuál
  // quiere cambiar mientras confirma.
  cambiandoIdentificacion = false
  cambioPendiente: Identificacion | null = null
  protected formatos = FORMATOS_DOCUMENTO_TEXTO
  protected readonly tamanoMaximoTexto = TAMANO_MAXIMO_DOCUMENTO_TEXTO
  protected readonly iconos = {
    documento: faFileLines,
    subir: faCloudArrowUp,
    camara: faCamera,
    carpeta: faFolderOpen,
    pdf: faFilePdf,
    imagen: faFileImage,
    borrar: faTrashCan,
    listo: faCheck,
    alerta: faExclamation,
    reintentar: faRotateRight,
    seguro: faShieldHalved,
    consejo: faLightbulb,
    cerrar: faXmark,
    info: faCircleInfo,
    abrir: faChevronDown,
    agregar: faPlus,
    descargar: faDownload,
    cambiar: faRightLeft,
    siguiente: faChevronRight
  }

  private idEtapa = ''
  // Cuántos archivos pide el paso y con qué etiqueta: los del paso o, en un
  // documento de identidad, los de la identificación elegida.
  private archivosPaso: ArchivosDelPaso = {}
  // De qué identificación son los archivos que tiene el servidor (sin guardada,
  // de la INE de siempre) y cuáles son: al volver a ella se ven otra vez.
  private identificacionGuardada: Identificacion = 'INE'
  private archivosGuardados: IFlowFile[] = []
  // La del paso como llegó (no cambia con las subidas): el motivo de rechazo y
  // el estado de `data` son de ella.
  private identificacionDelPaso: Identificacion = 'INE'
  private temporizadorRegreso: ReturnType<typeof setTimeout> | null = null
  private regresoEmitido = false
  private destruido = false
  private readonly urls = new Set<string>()
  private listaCargados: IRenglonArchivo[] = []
  private cacheFilas: IFilaLote[] = []
  // El backend dio el paso por completo con archivos aún en la cola: se celebra
  // (y corre el regreso de 4 s) hasta vaciarla.
  private completoPendiente = false
  private avisoCola = ''
  // Foco tras un cambio que quita de la pantalla el botón que se tocó.
  private enfocar: {destino: DestinoFoco; soloSiSePerdio: boolean} | null = null
  private filaPorVer: number | null = null

  // Sin LoaderService: su pantalla blanca de carga taparía la barra de avance y
  // el «Analizando tu documento…», que ahora viven dentro de la tarjeta.
  constructor(private flowDataService: ApiFlowDataService, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.aplicarReglasCarga()
      this.aplicarIdentificaciones(changes['data'])
      this.armarRenglones()
    }
  }

  // Documento de identidad: cuáles acepta, cuál está elegida y qué archivos pide.
  // Si llega otra vez el mismo paso con la misma identificación guardada, se
  // respeta la que se eligió aquí (aún sin subir nada).
  private aplicarIdentificaciones(cambio?: SimpleChange) {
    const paso = this.data
    const lista = Array.isArray(paso?.identificaciones)
      ? identificacionesDe({identificaciones: paso.identificaciones})
      : identificacionesDe(paso?.properties?.settings)
    const anterior = cambio?.previousValue as IFlow | undefined
    const mismoPaso = !!anterior && anterior.id === paso?.id && (anterior.identificacion ?? null) === (paso?.identificacion ?? null)
    const guardada = this.aceptada(paso?.identificacion, lista)
    const local = mismoPaso ? this.aceptada(this.identificacion, lista) : null
    this.identificaciones = lista
    this.identificacion = local ?? guardada ?? (lista.length === 1 ? lista[0] : null)
    this.identificacionGuardada = guardada ?? (lista.length === 1 ? lista[0] : 'INE')
    this.identificacionDelPaso = this.identificacionGuardada
    this.archivosGuardados = this.archivosDelServidor()
    this.cambiandoIdentificacion = false
    this.cambioPendiente = null
    const base = this.archivosBase()
    this.opcionesIdentificacion = lista.map(clave => ({
      clave,
      nombre: NOMBRE_IDENTIFICACION[clave],
      pide: PIDE_IDENTIFICACION[clave] ?? textoPideIne(archivosDeIdentificacion('INE', base)),
      icono: ICONO_IDENTIFICACION[clave]
    }))
    this.archivosPaso = this.archivosEfectivos()
  }

  private aceptada(valor: unknown, lista = this.identificaciones): Identificacion | null {
    const v = String(valor ?? '').trim().toUpperCase()
    return (lista as string[]).includes(v) ? v as Identificacion : null
  }

  // Los archivos del documento (los de la INE). getFlow manda los EFECTIVOS de la
  // guardada: si es otra, la base sale del documento del catálogo (properties).
  private archivosBase(): ArchivosDelPaso {
    const paso = this.data
    const guardada = this.aceptada(paso?.identificacion)
    if (!this.esIdentidad || !guardada || guardada === 'INE') {
      return {fileCount: paso?.fileCount, fileCountMin: paso?.fileCountMin, fileLabels: paso?.fileLabels}
    }
    const catalogo = paso?.properties
    return {
      fileCount: cantidadArchivos({fileCount: Number(catalogo?.fileCount)}),
      fileLabels: Array.isArray(catalogo?.fileLabels) ? catalogo!.fileLabels!.map(e => String(e ?? '')) : []
    }
  }

  private archivosEfectivos(): ArchivosDelPaso {
    const base = this.archivosBase()
    return this.esIdentidad && this.identificacion ? archivosDeIdentificacion(this.identificacion, base) : base
  }

  // Lo que ya tiene el servidor. Compat con un backend que aún no manda
  // `identificaciones` (la lista sale de settings): un documento subido como un
  // solo archivo (URL en value, sin files) cuenta como el archivo 0. Con el
  // backend nuevo `files` ya es la lista real (vacía = nada guardado; value
  // puede ser de la identificación que se descartó).
  private archivosDelServidor(): IFlowFile[] {
    const paso = this.data
    const archivos = paso?.files ?? []
    if (!this.esIdentidad || Array.isArray(paso?.identificaciones)) return archivos
    if (archivos.length || !paso?.value || paso.status === 'PENDING') return archivos
    return [{index: 0, status: paso.status, comment: paso.comment ?? '', url: paso.value}]
  }

  // Los guardados son de la identificación guardada: al cambiar a otra, el paso
  // empieza de cero (el servidor descarta los anteriores con la primera subida).
  private archivosDeLaElegida(): IFlowFile[] {
    if (!this.esIdentidad) return this.data?.files ?? []
    if (this.identificacion && this.identificacion !== this.identificacionGuardada) return []
    return this.archivosGuardados
  }

  private aplicarReglasCarga() {
    this.reglasCarga = reglasDeCarga(this.data?.properties?.settings)
    // Sin configurar, todo como siempre (mismos `accept` y el texto «JPG, PNG o PDF»).
    const deSiempre = this.reglasCarga.captura === 'AMBAS' && this.reglasCarga.extensiones.length === 3
    const imagenes = this.reglasCarga.mimes.filter((m) => m.startsWith('image/'))
    this.acceptCamara = deSiempre ? 'image/*' : (imagenes.length ? imagenes : ['image/jpeg', 'image/png']).join(',')
    this.acceptArchivo = deSiempre ? 'image/*,application/pdf' : this.reglasCarga.accept
    this.formatos = this.reglasCarga.extensiones.length === 3 ? FORMATOS_DOCUMENTO_TEXTO : textoFormatos(this.reglasCarga)
    this.tipoFotoCamara = this.reglasCarga.extensiones.includes('jpg') ? 'image/jpeg' : 'image/png'
    if (!this.reglasCarga.guia) this.camaraGuia = null
  }

  // El archivo es de un formato que acepta el backend y ESTE documento.
  private formatoValido(archivo: File): boolean {
    return tipoAceptado(archivo) && aceptaArchivo(this.reglasCarga, archivo.type, archivo.name)
  }

  ngAfterViewChecked() {
    if (this.enfocar) {
      const {destino, soloSiSePerdio} = this.enfocar
      this.enfocar = null
      const elemento = this.elementoPorEnfocar(destino)
      if (elemento && (!soloSiSePerdio || this.focoPerdido())) {
        elemento.focus({preventScroll: true})
        this.verSinBrinco(elemento)
      }
    }
    if (this.filaPorVer !== null) {
      const fila = this.host.nativeElement.querySelector<HTMLElement>(`#doc-lote-fila-${this.filaPorVer}`)
      this.filaPorVer = null
      if (fila) this.verSinBrinco(fila)
    }
  }

  private elementoPorEnfocar(destino: DestinoFoco): HTMLElement | undefined {
    switch (destino) {
      case 'accion': return this.accionLoteRef?.nativeElement ?? this.tituloLoteRef?.nativeElement
      // Tras elegir: la identificación elegida; con una sola tarjeta no hay
      // resumen, así que el foco va a la zona.
      case 'identidad': return this.identidadRef?.nativeElement ?? this.botonZonaRef?.nativeElement
      case 'selector': return this.selectorRef?.nativeElement
      case 'confirmar': return this.confirmarRef?.nativeElement
      default: return this.tituloLoteRef?.nativeElement ?? this.botonZonaRef?.nativeElement
    }
  }

  ngOnDestroy() {
    this.destruido = true
    this.colaActiva = false
    this.cancelarRegreso()
    this.revocarTodas()
  }

  // ---------- Lectura para la plantilla ----------

  // Un documento de identidad siempre va por renglones (cada archivo con su
  // fileIndex), aunque la identificación elegida pida uno.
  get esMultiarchivo(): boolean {
    return this.esIdentidad || cantidadArchivos(this.archivosPaso) > 1
  }

  // Por renglones con uno solo (el pasaporte): sin rejilla de tarjetas ni barra
  // de avance; el estado va en el chip del encabezado, como el paso de siempre.
  get unaSola(): boolean {
    return this.esMultiarchivo && this.renglones.length === 1
  }

  // ---------- Documento de identidad ----------

  get esIdentidad(): boolean {
    return this.identificaciones.length > 0
  }

  // «¿Qué identificación vas a subir?»: con 2 o más y sin elegir, o al cambiarla.
  get eligiendoIdentificacion(): boolean {
    return this.identificaciones.length >= 2 && !this.terminado && (!this.identificacion || this.cambiandoIdentificacion)
  }

  // La elegida a la vista, con «Cambiar identificación».
  get mostrarIdentificacion(): boolean {
    return this.identificaciones.length >= 2 && !!this.identificacion && !this.eligiendoIdentificacion && !this.terminado
  }

  // La que tiene archivos ahora: la elegida o, sin elegir, la de lo que ya está
  // en el servidor (un paso de antes: la INE).
  get identificacionActual(): Identificacion | null {
    if (this.identificacion) return this.identificacion
    return this.renglones.some(r => r.estado !== 'PENDIENTE') ? this.identificacionGuardada : null
  }

  // Elegida aquí y distinta de la del paso: lo que trae `data` (el motivo de
  // rechazo, el estado) es de la otra.
  get identificacionCambiada(): boolean {
    return this.esIdentidad && !!this.identificacion && this.identificacion !== this.identificacionDelPaso
  }

  nombreDe(clave: Identificacion | null | undefined): string {
    return clave ? NOMBRE_IDENTIFICACION[clave] : 'identificación'
  }

  get iconoIdentificacion(): IconDefinition {
    return this.identificacion ? ICONO_IDENTIFICACION[this.identificacion] : faIdCard
  }

  // «Si cambias a Pasaporte, se quitan los archivos de tu INE que ya subiste.»
  get textoConfirmarCambio(): string {
    const subidos = this.renglones.some(r => r.estado !== 'PENDIENTE')
    return `Si cambias a ${this.nombreDe(this.cambioPendiente)}, se quitan los archivos de tu ${this.nombreDe(this.identificacionActual)} que ${subidos ? 'ya subiste' : 'elegiste'}.`
  }

  porClave(_: number, opcion: IOpcionIdentificacion) {
    return opcion.clave
  }

  get archivosCargados(): number {
    return this.renglones.filter(r => r.estado === 'CARGADO').length
  }

  get archivosRechazados(): number {
    return this.renglones.filter(r => r.estado === 'RECHAZADO').length
  }

  // Con cuántos pasa el paso: fileCountMin (de 1 a N); sin rango o con un
  // backend que no lo manda, todos.
  get minimo(): number {
    return this.esMultiarchivo ? minimoArchivos(this.archivosPaso) : 1
  }

  get tieneRango(): boolean {
    return this.esMultiarchivo && this.minimo < this.renglones.length
  }

  // Los renglones después del mínimo («5.º» y «6.º» de «de 4 a 6»).
  esOpcional(renglon: IRenglonArchivo): boolean {
    return this.tieneRango && renglon.index >= this.minimo
  }

  // La misma regla del backend: el mínimo cargado y ninguno rechazado.
  get completoLocal(): boolean {
    return this.archivosRechazados === 0 && this.archivosCargados >= this.minimo
  }

  // Lo que falta para completar: los que no llegan al mínimo y, en todo caso,
  // los rechazados (hay que corregirlos aunque ya estén los necesarios).
  get faltanNecesarios(): number {
    return Math.max(0, this.minimo - this.archivosCargados, this.archivosRechazados)
  }

  get quedanOpcionales(): boolean {
    return this.tieneRango && this.renglones.some(r => r.estado !== 'CARGADO')
  }

  // «Sube al menos 4 de 6 recibos; los demás son opcionales.»
  get textoRango(): string {
    return `Sube al menos ${this.minimo} de ${this.renglones.length} ${this.sustantivo}s; los demás son opcionales.`
  }

  get tituloCompletoMinimo(): string {
    return this.minimo === 1 ? '¡Listo! Ya tienes el necesario' : `¡Listo! Ya tienes los ${this.minimo} necesarios`
  }

  get textoCompletoMinimo(): string {
    return this.mensajeExito || `Si quieres, sube los opcionales (hasta ${this.renglones.length}); si no, continúa con tus pasos.`
  }

  // Encabezado: «2 de 6 listos»; con rango, «3 de 4 necesarios (hasta 6)» o
  // «4 de 6 · completo» (las palabras de la lista de pasos y del backend).
  get avanceFuerte(): string {
    const cargados = this.archivosCargados
    const total = this.renglones.length
    if (this.tieneRango && !this.completoLocal && cargados < this.minimo) return `${cargados} de ${this.minimo}`
    return `${cargados} de ${total}`
  }

  get avanceResto(): string {
    if (!this.tieneRango) return 'listos'
    if (this.completoLocal) return '· completo'
    if (this.archivosCargados < this.minimo) return `necesarios (hasta ${this.renglones.length})`
    return `(necesitas ${this.minimo})`
  }

  // Con rango la barra se llena con el mínimo (el paso ya cuenta como completo).
  get metaAvance(): number {
    return this.tieneRango ? this.minimo : this.renglones.length
  }

  // Lo que ya cuenta para la meta: sin rango, los cargados de siempre; con rango
  // no llega al 100 % mientras haya uno rechazado aunque ya estén los necesarios.
  get valorAvance(): number {
    return Math.max(0, this.metaAvance - this.faltanNecesarios)
  }

  get porcentajeAvance(): number {
    if (!this.renglones.length || !this.metaAvance) return 0
    return Math.round(100 * this.valorAvance / this.metaAvance)
  }

  // A dónde va lo que se elija en la zona de carga.
  get activo(): IRenglonArchivo {
    if (!this.esMultiarchivo) return this.unico
    return this.renglones[this.indiceActivo] ?? this.renglones[0] ?? this.unico
  }

  // Subiendo uno suelto o con la cola en marcha: nada de la selección se toca.
  get ocupado(): boolean {
    return this.subiendo || this.colaActiva
  }

  // Archivos elegidos y aún sin subir.
  get seleccionados(): number {
    return this.renglones.filter(r => !!r.archivo).length
  }

  // Tarjetas que faltan (pendientes o rechazadas) y todavía sin archivo elegido.
  get libres(): number {
    return this.renglones.filter(r => r.estado !== 'CARGADO' && !r.archivo).length
  }

  // Resumen de varios (o la cola): con dos o más elegidos, o con uno que no es
  // el de la tarjeta activa. Con uno solo en la activa queda la vista de siempre.
  get mostrarLote(): boolean {
    if (!this.esMultiarchivo || this.terminado) return false
    if (this.cola.length) return true
    const elegidos = this.seleccionados
    return elegidos >= 2 || (elegidos === 1 && !this.activo.archivo)
  }

  get mostrarElegido(): boolean {
    if (!this.activo.archivo) return false
    return !this.esMultiarchivo || (this.seleccionados === 1 && !this.cola.length)
  }

  get mostrarZona(): boolean {
    return !this.activo.archivo && !this.colaActiva
  }

  // «Agregar más» cuando la zona no está a la vista y aún faltan tarjetas.
  get puedeAgregar(): boolean {
    return this.esMultiarchivo && !this.ocupado && !this.mostrarZona && this.libres > 0
  }

  // La tarjeta resaltada: la que sube, la que se está editando o la activa. Con
  // todo elegido y sin zona no hay destino: ninguna (no parece «seleccionada»).
  get tarjetaResaltada(): number {
    if (this.subiendo) return this.indiceActivo
    if (this.edicion !== null && this.mostrarLote) return this.edicion
    if (this.mostrarLote && !this.mostrarZona && !this.cola.length) return -1
    return this.indiceActivo
  }

  // Compatibilidad con el paso de un archivo de siempre.
  get hasFile(): boolean {
    return !!this.unico.archivo
  }

  get file(): File | null {
    return this.unico.archivo
  }

  /**
   * Archivo que el usuario puede descargar en este paso (ej. los términos y condiciones para firmar).
   * Viene en InputCatalog.settings.downloadUrl (settings es un JSON en texto).
   */
  get downloadUrl(): string {
    try {
      const url = JSON.parse(this.data?.properties?.settings || '{}')?.downloadUrl
      return typeof url === 'string' ? url : ''
    } catch (e) {
      return ''
    }
  }

  async download() {
    const url = this.downloadUrl
    if (!url) return
    if (CapacitorCore.Capacitor.getPlatform() === 'web') {
      window.open(url, '_blank')
    } else {
      await CapacitorBrowser.Browser.open({url})
    }
  }

  get titulo(): string {
    return this.data?.properties?.title || 'Carga tu documento'
  }

  get subtitulo(): string {
    return this.data?.properties?.subtitle || ''
  }

  // Hoy el único paso multiarchivo son los recibos de nómina.
  get sustantivo(): string {
    return /PAYROLL|NOMINA|NÓMINA/i.test(this.data?.properties?.name ?? '') ? 'recibo' : 'archivo'
  }

  get tituloZona(): string {
    const activo = this.activo
    if (!this.esMultiarchivo) {
      return activo.estado === 'PENDIENTE' ? 'Toca para subir tu documento' : 'Toca para reemplazar tu documento'
    }
    const opcional = this.esOpcional(activo) ? ' (opcional)' : ''
    return `${activo.estado === 'CARGADO' ? 'Reemplaza' : 'Sube'} tu ${this.sustantivo} «${activo.etiqueta}»${opcional}`
  }

  get subtituloZona(): string {
    // «Solo cámara»: no se elige ni se arrastra nada, se toma la foto.
    if (!this.reglasCarga.conArchivo) {
      return this.reglasCarga.guia ? 'Toma la foto con la cámara, dentro del recuadro' : 'Toma la foto con la cámara'
    }
    if (!this.esMultiarchivo) return 'o arrastra el archivo aquí'
    return this.libres > 1 ? 'Puedes elegir o arrastrar varios a la vez' : 'Toca aquí o arrastra el archivo'
  }

  get textoElegirArchivos(): string {
    return this.esMultiarchivo && !this.unaSola ? 'Elegir archivos' : 'Elegir archivo'
  }

  get tituloListo(): string {
    return this.esMultiarchivo && !this.unaSola ? '¡Listo! Recibimos tus documentos' : '¡Listo! Recibimos tu documento'
  }

  get textoBotonSubir(): string {
    const activo = this.activo
    const verbo = activo.estado === 'PENDIENTE' ? 'Subir' : 'Reemplazar'
    return this.esMultiarchivo ? `${verbo} ${this.sustantivo} «${activo.etiqueta}»` : `${verbo} documento`
  }

  get textoAgregar(): string {
    return `Agregar más ${this.sustantivo}s`
  }

  get estadoUnicoVisual(): EstadoVisual {
    const renglon = this.unaSola ? this.renglones[0] : this.unico
    if (this.terminado) return 'listo'
    if (renglon.archivo) return 'activo'
    if (renglon.estado === 'CARGADO') return 'listo'
    return renglon.estado === 'RECHAZADO' ? 'rechazado' : 'pendiente'
  }

  get textoEstadoUnico(): string {
    switch (this.estadoUnicoVisual) {
      case 'listo': return this.terminado ? 'Listo' : 'Cargado'
      case 'activo': return 'Por subir'
      case 'rechazado': return 'Rechazado'
      default: return 'Pendiente'
    }
  }

  // Lo que ya tiene archivo (cargado o rechazado), para «Tus documentos». Se
  // reusa el mismo arreglo mientras no cambie, para que *ngFor no vea uno nuevo
  // en cada ciclo de detección.
  get documentosCargados(): IRenglonArchivo[] {
    const lista = this.esMultiarchivo
      ? this.renglones.filter(r => r.estado !== 'PENDIENTE')
      : this.unico.estado === 'PENDIENTE' ? [] : [this.unico]
    const igual = lista.length === this.listaCargados.length && lista.every((r, i) => r === this.listaCargados[i])
    if (!igual) this.listaCargados = lista
    return this.listaCargados
  }

  // Renglones del resumen: los de la cola (con su estado) o, sin cola, los
  // elegidos en el orden de las tarjetas. Mismo arreglo mientras no cambie nada.
  get filasLote(): IFilaLote[] {
    const filas = this.armarFilasLote()
    const igual = filas.length === this.cacheFilas.length && filas.every((f, i) => mismaFila(f, this.cacheFilas[i]))
    if (!igual) this.cacheFilas = filas
    return this.cacheFilas
  }

  get colaHechos(): number {
    return this.cola.filter(i => i.estado === 'guardado' || i.estado === 'error' || i.estado === 'cancelado').length
  }

  get colaEnEspera(): number {
    return this.cola.filter(i => i.estado === 'espera').length
  }

  // Lo que quedó de la cola y aún tiene su archivo: se reintenta o se sube.
  get pendientesDeCola(): IItemCola[] {
    return this.cola.filter(i => (i.estado === 'error' || i.estado === 'cancelado') && !!this.renglones[i.index]?.archivo)
  }

  get soloErrores(): boolean {
    const pendientes = this.pendientesDeCola
    return pendientes.length > 0 && pendientes.every(i => i.estado === 'error')
  }

  get tituloLote(): string {
    if (this.colaActiva) return `Subiendo ${Math.min(this.colaHechos + 1, this.cola.length)} de ${this.cola.length}`
    if (this.cola.length) return this.resumenCola
    const elegidos = this.seleccionados
    return `${elegidos} ${elegidos === 1 ? 'archivo listo' : 'archivos listos'} para subir`
  }

  get subtituloLote(): string {
    if (this.colaActiva) {
      if (!this.cancelando) return 'Van uno por uno. No cierres esta pantalla.'
      const actual = this.itemEnCurso
      return actual ? `Terminamos «${this.etiquetaDe(actual.index)}» y nos detenemos.` : 'Nos detenemos.'
    }
    if (this.cola.length) {
      if (this.cola.some(i => i.estado === 'error')) return 'Revisa el motivo y vuelve a intentarlo.'
      if (this.cola.some(i => i.estado === 'cancelado')) return 'Los que no se subieron siguen aquí: súbelos cuando quieras.'
      const faltan = this.renglones.filter(r => r.estado !== 'CARGADO').length
      if (!this.tieneRango) return faltan ? `Te ${faltan === 1 ? 'falta' : 'faltan'} ${this.contar(faltan)}.` : ''
      // Con rango: lo que falta para el mínimo; ya cubierto, lo demás es opcional.
      const necesarios = this.faltanNecesarios
      if (necesarios) return `Te ${necesarios === 1 ? 'falta' : 'faltan'} ${this.contar(necesarios)}.`
      return faltan ? `Si quieres, puedes subir ${this.contar(faltan)} más.` : ''
    }
    return 'Van en este orden. Toca uno para cambiarlo o quitarlo.'
  }

  // «5 de 6 se guardaron · 1 por reintentar · 2 sin subir».
  get resumenCola(): string {
    const guardados = this.cola.filter(i => i.estado === 'guardado').length
    const errores = this.cola.filter(i => i.estado === 'error').length
    const sinSubir = this.cola.filter(i => i.estado === 'cancelado').length
    const partes = [`${guardados} de ${this.cola.length} ${guardados === 1 ? 'se guardó' : 'se guardaron'}`]
    if (errores) partes.push(`${errores} por reintentar`)
    if (sinSubir) partes.push(`${sinSubir} sin subir`)
    return partes.join(' · ')
  }

  get textoBotonLote(): string {
    if (!this.cola.length) return `Subir ${this.contar(this.seleccionados)}`
    const pendientes = this.pendientesDeCola.length
    if (!pendientes) return ''
    if (this.soloErrores) return `Reintentar ${this.contar(pendientes)}`
    return pendientes === 1 ? 'Subir el que falta' : `Subir los ${pendientes} que faltan`
  }

  get textoCancelar(): string {
    if (!this.cancelando) return 'Cancelar los que faltan'
    const actual = this.itemEnCurso
    return actual ? `Nos detenemos al terminar «${this.etiquetaDe(actual.index)}»` : 'Deteniendo…'
  }

  get tituloDescartados(): string {
    const n = this.archivosDescartados.length
    return n === 1 ? '1 archivo no se pudo usar' : `${n} archivos no se pudieron usar`
  }

  // Texto de la región aria-live (subiendo / cola / listo / error).
  get anuncio(): string {
    if (this.terminado) return this.tituloListo
    if (this.colaActiva) {
      const actual = this.itemEnCurso
      const verbo = this.cancelando ? 'Deteniendo la subida' : this.analizando ? 'Analizando' : 'Subiendo'
      const cuenta = `${Math.min(this.colaHechos + 1, this.cola.length)} de ${this.cola.length}`
      return `${this.avisoCola}${verbo} ${cuenta}${actual ? ': ' + this.etiquetaDe(actual.index) : ''}`
    }
    if (this.cola.length) return this.resumenCola
    if (this.subiendo) return this.analizando ? 'Analizando tu documento' : 'Subiendo tu documento'
    if (this.activo.errorSubida) return this.activo.errorSubida
    if (this.mensajeExito || this.avisoValidacion) return this.mensajeExito || this.avisoValidacion
    if (!this.mostrarLote) return this.completoMinimo ? this.tituloCompletoMinimo : ''
    const partes = [this.tituloLote]
    if (this.archivosDescartados.length) partes.push(this.tituloDescartados)
    if (this.avisoSobrantes) partes.push(this.avisoSobrantes.titulo)
    return partes.join('. ')
  }

  textoEstado(renglon: IRenglonArchivo): string {
    if (this.subiendo && renglon.index === this.indiceActivo) return this.analizando ? 'Analizando…' : 'Subiendo…'
    if (renglon.archivo) {
      if (this.colaActiva && this.cola.some(i => i.index === renglon.index && i.estado === 'espera')) return 'En espera'
      return renglon.errorSubida ? 'Por reintentar' : 'Por subir'
    }
    if (renglon.estado === 'CARGADO') return 'Listo'
    return renglon.estado === 'RECHAZADO' ? 'Rechazado' : 'Pendiente'
  }

  etiquetaTarjeta(renglon: IRenglonArchivo): string {
    const archivo = renglon.archivo ? `, ${renglon.archivo.name}` : ''
    const opcional = this.esOpcional(renglon) ? ' (opcional)' : ''
    return `${renglon.etiqueta}${opcional}: ${this.textoEstado(renglon)}${archivo}`
  }

  textoFila(fila: IFilaLote): string {
    switch (fila.estado) {
      case 'espera': return 'En espera'
      case 'subiendo': return this.analizando ? 'Analizando…' : `Subiendo… ${this.progreso} %`
      case 'guardado': return 'Se guardó'
      case 'error': return this.cola.length ? 'No se pudo subir' : 'Por reintentar'
      case 'cancelado': return 'Sin subir'
      default: return 'Listo para subir'
    }
  }

  // Cambiar o quitar: solo lo que aún tiene su archivo elegido y sin cola en marcha.
  puedeEditar(fila: IFilaLote): boolean {
    return !this.ocupado && (fila.estado === 'listo' || fila.estado === 'error' || fila.estado === 'cancelado')
  }

  // Con la cola en marcha, el renglón que falló se vuelve a formar; ya detenida,
  // lo reintenta el botón principal («Reintentar 1 recibo»).
  puedeReintentar(fila: IFilaLote): boolean {
    return fila.estado === 'error' && this.colaActiva && !this.cancelando && !this.terminado
  }

  tipoDe(archivo: File | null | undefined): TipoDocumento {
    return tipoDeArchivo(archivo)
  }

  tipoFila(renglon: IRenglonArchivo): TipoDocumento {
    return renglon.subido?.tipo ?? tipoDeArchivo({name: renglon.url ?? ''})
  }

  iconoTipo(tipo: TipoDocumento): IconDefinition {
    if (tipo === 'pdf') return this.iconos.pdf
    return tipo === 'imagen' ? this.iconos.imagen : this.iconos.documento
  }

  iconoTarjeta(renglon: IRenglonArchivo): IconDefinition {
    if (renglon.archivo) return this.iconoTipo(tipoDeArchivo(renglon.archivo))
    return renglon.subido ? this.iconoTipo(renglon.subido.tipo) : this.iconos.documento
  }

  // Miniatura en la tarjeta: la del archivo elegido o, si ya se subió en esta
  // sesión, la que pasó a «Tus documentos».
  miniaturaTarjeta(renglon: IRenglonArchivo): string | null {
    return renglon.archivo ? renglon.vistaPrevia ?? null : renglon.subido?.miniatura ?? null
  }

  // Solo el elegido se pinta como PDF; uno ya cargado conserva su verde.
  esPdfTarjeta(renglon: IRenglonArchivo): boolean {
    return !!renglon.archivo && tipoDeArchivo(renglon.archivo) === 'pdf'
  }

  tamanoTexto(bytes: number): string {
    return formatoTamano(bytes)
  }

  metaFila(renglon: IRenglonArchivo): string {
    const fecha = formatoFechaSubida(renglon.fechaSubida)
    let cuando = fecha ? `Subido el ${fecha}` : ''
    if (!cuando) cuando = renglon.subido ? 'Subido hace un momento' : renglon.estado === 'RECHAZADO' ? 'Necesita una nueva versión' : 'Ya lo tenemos'
    return renglon.subido ? `${cuando} · ${formatoTamano(renglon.subido.tamano)}` : cuando
  }

  porIndice(_: number, renglon: {index: number}) {
    return renglon.index
  }

  // ---------- Documento de identidad: elegir y cambiar ----------

  // Una tarjeta de «¿Qué identificación vas a subir?». Si ya hay archivos de otra
  // (subidos o elegidos), primero se confirma aquí mismo.
  elegirIdentificacion(clave: Identificacion) {
    if (this.ocupado || this.terminado || !this.identificaciones.includes(clave)) return
    const actual = this.identificacionActual
    const conArchivos = this.renglones.some(r => r.estado !== 'PENDIENTE' || !!r.archivo)
    if (clave === actual || !actual || !conArchivos) {
      this.aplicarIdentificacion(clave)
      return
    }
    this.cambioPendiente = clave
    this.enfocar = {destino: 'confirmar', soloSiSePerdio: false}
  }

  abrirCambioIdentificacion() {
    if (this.ocupado || this.terminado || this.identificaciones.length < 2) return
    this.cambiandoIdentificacion = true
    this.cambioPendiente = null
    this.camaraGuia = null
    this.edicion = null
    this.enfocar = {destino: 'selector', soloSiSePerdio: false}
  }

  // «Seguir con mi INE» del selector: sin cambiar nada.
  cerrarCambioIdentificacion() {
    this.cambiandoIdentificacion = false
    this.cambioPendiente = null
    this.enfocar = {destino: 'identidad', soloSiSePerdio: false}
  }

  confirmarCambioIdentificacion() {
    if (!this.cambioPendiente || this.ocupado || this.terminado) return
    this.aplicarIdentificacion(this.cambioPendiente)
  }

  // «Seguir con mi INE» de la confirmación. Si aún no había elegido (un paso de
  // antes con archivos de la INE), se queda con esa.
  cancelarCambioIdentificacion() {
    const actual = this.identificacionActual
    if (!this.identificacion && actual) {
      this.aplicarIdentificacion(actual)
      return
    }
    this.cerrarCambioIdentificacion()
  }

  // Con otra identificación los renglones empiezan de cero (los suyos si es la
  // guardada); lo elegido y la cola de la anterior se descartan.
  private aplicarIdentificacion(clave: Identificacion) {
    this.cambioPendiente = null
    this.cambiandoIdentificacion = false
    this.enfocar = {destino: 'identidad', soloSiSePerdio: false}
    if (clave === this.identificacion) return
    this.identificacion = clave
    this.archivosPaso = this.archivosEfectivos()
    this.reiniciarCarga()
    this.camaraGuia = null
    this.armarFilas()
  }

  // ---------- Selección ----------

  seleccionarTarjeta(renglon: IRenglonArchivo) {
    if (this.ocupado || this.terminado) return
    this.avisoValidacion = ''
    this.mensajeExito = ''
    // Con el resumen a la vista, tocar una tarjeta con archivo abre su renglón
    // para cambiarlo o quitarlo; la zona sigue apuntando a la que falta.
    if (this.esMultiarchivo && renglon.archivo && this.mostrarLote) {
      this.edicion = renglon.index
      this.filaPorVer = renglon.index
      return
    }
    this.edicion = null
    this.indiceActivo = renglon.index
  }

  reemplazar(renglon: IRenglonArchivo) {
    if (this.ocupado || this.terminado) return
    if (renglon !== this.unico) this.seleccionarTarjeta(renglon)
    const area = this.areaCarga?.nativeElement
    if (area && typeof area.scrollIntoView === 'function') {
      area.scrollIntoView({behavior: this.sinMovimiento() ? 'auto' : 'smooth', block: 'center'})
    }
  }

  // Los <input type="file"> de «Tomar foto» y «Elegir archivo(s)». En
  // multiarchivo «Elegir archivos» acepta varios y se reparten en orden.
  alElegir(evento: Event) {
    if (!this.esMultiarchivo) this.seleccionarArchivo(this.activo, evento)
    else this.repartirArchivos(this.extraerArchivos(evento))
  }

  // Varios archivos (multiarchivo): cada válido va, en el orden en que llegan, a
  // la siguiente tarjeta que falte (pendiente o rechazada) empezando por la
  // activa. Los inválidos no ocupan lugar y se avisa cuáles y por qué; los que
  // sobran, también. Uno solo se elige como siempre, para la tarjeta activa.
  repartirArchivos(archivos: File[]) {
    if (!archivos.length || this.ocupado || this.terminado) return
    if (!this.esMultiarchivo) {
      this.seleccionarArchivo(this.activo, {files: archivos})
      return
    }
    if (archivos.length === 1) {
      this.elegirUno(archivos[0])
      return
    }
    this.avisoValidacion = ''
    this.mensajeExito = ''
    const validos: File[] = []
    const descartados: IArchivoDescartado[] = []
    for (const archivo of archivos) {
      const motivo = this.motivoDescarte(archivo)
      if (motivo) descartados.push({nombre: archivo.name, motivo})
      else validos.push(archivo)
    }
    const destinos = this.destinosLibres()
    const usados = validos.slice(0, destinos.length)
    usados.forEach((archivo, i) => this.asignar(destinos[i], archivo))
    const sobrantes = validos.slice(destinos.length)
    this.archivosDescartados = descartados
    this.avisoSobrantes = sobrantes.length ? this.textoSobrantes(archivos.length, destinos.length, sobrantes) : null
    if (!usados.length) return
    this.cola = []
    this.edicion = null
    this.pasarALaQueFalta()
  }

  // Uno solo en multiarchivo: a la activa como siempre o, si la activa ya tiene
  // uno elegido («Agregar más»), a la siguiente que falte.
  private elegirUno(archivo: File): void {
    const destino = this.activo.archivo ? this.destinosLibres()[0] : this.activo
    this.archivosDescartados = []
    this.avisoSobrantes = null
    if (!destino) {
      this.avisoSobrantes = this.textoSobrantes(1, 0, [archivo])
      return
    }
    this.seleccionarArchivo(destino, {files: [archivo]})
    if (destino.archivo === archivo && this.seleccionados >= 2) this.pasarALaQueFalta()
  }

  // Con varios elegidos, la zona pasa a la siguiente que falte; si ya no falta
  // ninguna, se va y el foco se queda en el resumen.
  private pasarALaQueFalta() {
    const siguiente = this.destinosLibres()[0]
    if (siguiente) this.indiceActivo = siguiente.index
    this.enfocar = {destino: 'titulo', soloSiSePerdio: !!siguiente}
  }

  // Acepta {files} (como antes), el evento change de un input o un drop. El tope
  // de tamaño y el tipo se revisan aquí con un aviso en línea; si no pasa, no se
  // toca lo que ya estaba elegido.
  seleccionarArchivo(renglon: IRenglonArchivo, evento: any) {
    const archivo = this.extraerArchivo(evento)
    if (!archivo || this.ocupado || this.terminado) return
    if (renglon !== this.unico) this.indiceActivo = renglon.index
    this.mensajeExito = ''
    const nombre = this.esMultiarchivo ? `El archivo para «${renglon.etiqueta}»` : `«${archivo.name}»`
    if (!this.formatoValido(archivo)) {
      this.avisoValidacion = this.reglasCarga.extensiones.length === 3
        ? `${nombre} no es una foto (JPG o PNG) ni un PDF. Elige otro archivo.`
        : `${nombre} no se acepta: este documento solo acepta ${this.formatos}. Elige otro archivo.`
      return
    }
    if (archivo.size > TAMANO_MAXIMO_DOCUMENTO) {
      this.avisoValidacion = `${nombre} pesa más de ${TAMANO_MAXIMO_DOCUMENTO_TEXTO}. Prueba con una foto o un PDF más ligero.`
      return
    }
    this.avisoValidacion = ''
    this.asignar(renglon, archivo)
    if (this.esMultiarchivo) this.olvidarResumen()
  }

  select(event: any) {
    this.seleccionarArchivo(this.unico, event)
  }

  quitarArchivo(renglon: IRenglonArchivo) {
    if (this.ocupado) return
    this.revocar(renglon.vistaPrevia)
    renglon.vistaPrevia = null
    renglon.archivo = null
    renglon.errorSubida = ''
    if (this.esMultiarchivo) this.olvidarResumen()
  }

  clear() {
    this.quitarArchivo(this.unico)
  }

  // ---------- Resumen de varios: cambiar o quitar antes de subir ----------

  editarFila(indice: number) {
    if (this.ocupado || this.terminado) return
    this.edicion = this.edicion === indice ? null : indice
  }

  cambiarArchivo(indice: number) {
    const renglon = this.renglones[indice]
    if (!renglon || this.ocupado || this.terminado) return
    this.edicion = indice
    // «Solo cámara» con recuadro: «Cambiar» es volver a tomar la foto.
    if (!this.reglasCarga.conArchivo && this.reglasCarga.guia) this.abrirCamara('reemplazo')
    else this.inputReemplazoRef?.nativeElement.click()
  }

  // El input de «Cambiar»: uno solo, para el renglón abierto. La zona se queda
  // apuntando a donde estaba (no se mueve nada de la pantalla).
  alReemplazar(evento: Event) {
    // Se lee (y se limpia el input) siempre; con la cola en marcha no se toca nada.
    this.reemplazarCon(this.extraerArchivo(evento))
  }

  private reemplazarCon(archivo: File | null) {
    const renglon = this.edicion === null ? undefined : this.renglones[this.edicion]
    if (!renglon || !archivo || this.ocupado || this.terminado) return
    const zona = this.renglones[this.indiceActivo]
    const zonaLibre = !!zona && !zona.archivo
    this.seleccionarArchivo(renglon, {files: [archivo]})
    if (zonaLibre) this.indiceActivo = zona.index
    this.edicion = renglon.index
  }

  quitarDeLote(indice: number) {
    const renglon = this.renglones[indice]
    if (!renglon || this.ocupado || this.terminado) return
    this.quitarArchivo(renglon)
    this.edicion = null
    // La zona apunta a la que se vació, para elegir otro si se quiere.
    this.indiceActivo = indice
    this.enfocar = {destino: 'titulo', soloSiSePerdio: true}
  }

  // ---------- Cámara con recuadro (Omnicanal: settings.guia) ----------

  // El recuadro del visor: el configurado o, en un documento de identidad, el de
  // la elegida (credencial o pasaporte). null = sin recuadro.
  get guiaCamara(): RecuadroCamara | null {
    if (!this.reglasCarga.guia) return null
    return this.esIdentidad ? guiaDeIdentificacion(this.identificacion) : this.reglasCarga.guia
  }

  // En el visor (y en el nombre de la foto): «Pasaporte · Hoja de datos».
  get tituloCamara(): string {
    return this.esIdentidad && this.identificacion ? NOMBRE_IDENTIFICACION[this.identificacion] : this.titulo
  }

  // «Tomar foto»: con recuadro abre la cámara de la liga; sin él, la del
  // celular como siempre (el input con capture).
  abrirCamara(destino: 'elegir' | 'reemplazo' = 'elegir') {
    if (!this.reglasCarga.guia) {
      this.inputDeCamara(destino)?.click()
      return
    }
    if (this.ocupado || this.terminado) return
    this.camaraGuia = {destino, etiqueta: this.etiquetaParaCamara(destino)}
  }

  // La foto recortada entra por el mismo camino que la de un input.
  alFotoDeCamara(foto: File) {
    const destino = this.camaraGuia?.destino ?? 'elegir'
    this.camaraGuia = null
    if (destino === 'reemplazo') this.reemplazarCon(foto)
    else if (!this.esMultiarchivo) this.seleccionarArchivo(this.activo, {files: [foto]})
    else this.repartirArchivos([foto])
  }

  // Aquí no se pudo abrir la cámara (permiso, sin HTTPS, WebView): la del
  // celular, en el mismo clic para no perder el gesto del usuario.
  alCamaraDelEquipo() {
    const destino = this.camaraGuia?.destino ?? 'elegir'
    this.camaraGuia = null
    this.inputDeCamara(destino)?.click()
  }

  private inputDeCamara(destino: 'elegir' | 'reemplazo'): HTMLInputElement | undefined {
    return (destino === 'reemplazo' ? this.inputReemplazoRef : this.inputCamaraRef)?.nativeElement
  }

  // «Anverso», «Último»…: la cara que va a recibir la foto (vacío en pasos de uno).
  private etiquetaParaCamara(destino: 'elegir' | 'reemplazo'): string {
    if (!this.esMultiarchivo) return ''
    if (destino === 'reemplazo') return this.edicion === null ? '' : this.renglones[this.edicion]?.etiqueta ?? ''
    const renglon = this.activo.archivo ? this.destinosLibres()[0] : this.activo
    return renglon?.etiqueta ?? ''
  }

  // ---------- Arrastrar y soltar (escritorio) ----------

  alArrastrar(evento: DragEvent) {
    evento.preventDefault()
    if (this.ocupado || this.terminado) return
    if (evento.dataTransfer) evento.dataTransfer.dropEffect = 'copy'
    this.arrastrando = true
  }

  alSalirArrastre(evento: DragEvent) {
    const zona = evento.currentTarget as HTMLElement | null
    const destino = evento.relatedTarget as Node | null
    if (zona && destino && zona.contains(destino)) return
    this.arrastrando = false
  }

  alSoltar(evento: DragEvent) {
    evento.preventDefault()
    this.arrastrando = false
    // «Solo foto con la cámara»: arrastrar un archivo no es tomar la foto.
    if (!this.reglasCarga.conArchivo) {
      this.avisoValidacion = 'Este documento se sube tomando una foto con la cámara.'
      return
    }
    const archivos = this.extraerArchivos({files: evento.dataTransfer?.files})
    if (this.esMultiarchivo) this.repartirArchivos(archivos)
    else this.seleccionarArchivo(this.activo, {files: archivos})
  }

  // ---------- Subida ----------

  // Uno suelto: nunca con la cola en marcha (ella decide cuál sigue).
  subirActivo() {
    if (this.colaActiva) return
    this.subirArchivo(this.activo)
  }

  reintentar() {
    if (this.colaActiva) return
    this.subirArchivo(this.activo)
  }

  // Paso de un archivo: el mismo FormData de siempre, sin fileIndex.
  save() {
    const renglon = this.unico
    if (!renglon.archivo || this.subiendo || this.terminado) return
    const form = new FormData()
    form.append('document', renglon.archivo)
    form.append('idDocument', this.data.id)
    if (this.data.userDocumentId) form.append('idStage', this.data.userDocumentId)
    if (this.pushingId) form.append('pushingId', this.pushingId)
    this.enviar(renglon, form, r => {
      if (r.error) {
        renglon.errorSubida = r.msg || 'No pudimos subir tu documento. Intenta de nuevo.'
        return
      }
      this.registrarSubida(renglon)
      this.mensajeExito = r.msg
      this.terminar()
    })
  }

  // Uno a la vez: el backend analiza cada archivo con IA y guarda files[] del
  // mismo FlowData; dos subidas simultáneas se pisarían el arreglo.
  subirArchivo(renglon: IRenglonArchivo) {
    if (renglon === this.unico) return this.save()
    if (!renglon.archivo || this.subiendo || this.terminado) return
    // Documento de identidad: nunca sin saber cuál (el servidor la exige con 2 o más).
    if (this.esIdentidad && !this.identificacion) return
    this.indiceActivo = renglon.index
    const form = new FormData()
    form.append('document', renglon.archivo)
    form.append('idDocument', this.data.id)
    form.append('fileIndex', String(renglon.index))
    if (this.esIdentidad && this.identificacion) form.append('identificacion', this.identificacion)
    if (this.idEtapa) form.append('idStage', this.idEtapa)
    if (this.pushingId) form.append('pushingId', this.pushingId)
    this.enviar(renglon, form, r => this.alResponderMultiarchivo(renglon, r))
  }

  // «Subir N recibos»: forma la cola con lo elegido, en el orden de las tarjetas.
  subirSeleccion() {
    if (!this.esMultiarchivo || this.ocupado || this.terminado) return
    const elegidos = this.renglones.filter(r => !!r.archivo)
    if (!elegidos.length) return
    this.cola = elegidos.map(r => ({
      index: r.index,
      nombre: r.archivo!.name,
      tamano: r.archivo!.size,
      tipo: tipoDeArchivo(r.archivo),
      estado: 'espera',
      error: ''
    }))
    this.completoPendiente = false
    this.iniciarCola()
  }

  // Del resumen: vuelve a formar lo que falló o no se subió (con su archivo).
  reanudarCola() {
    if (this.ocupado || this.terminado) return
    const pendientes = this.pendientesDeCola
    if (!pendientes.length) return
    for (const item of pendientes) {
      item.estado = 'espera'
      item.error = ''
    }
    this.iniciarCola()
  }

  // «Reintentar» de un renglón: con la cola en marcha se forma otra vez (va en
  // cuanto termine el que sube); con la cola detenida, la reanuda solo con él.
  reintentarFila(indice: number) {
    if (this.terminado || this.cancelando || (this.subiendo && !this.colaActiva)) return
    const item = this.cola.find(i => i.index === indice && i.estado === 'error')
    if (!item || !this.renglones[indice]?.archivo) return
    item.estado = 'espera'
    item.error = ''
    if (!this.colaActiva) this.iniciarCola()
  }

  // Nunca aborta uno a medias: el que va termina y los demás se quedan sin subir.
  cancelarCola() {
    if (!this.colaActiva || this.cancelando) return
    this.cancelando = true
  }

  continuar() {
    this.cancelarRegreso()
    this.emitirRegreso()
  }

  private iniciarCola() {
    this.colaActiva = true
    this.cancelando = false
    this.edicion = null
    this.avisoCola = ''
    this.avisoValidacion = ''
    this.archivosDescartados = []
    this.avisoSobrantes = null
    this.mensajeExito = ''
    this.enfocar = {destino: 'accion', soloSiSePerdio: false}
    this.siguienteEnCola()
  }

  private siguienteEnCola(): void {
    if (!this.colaActiva) return
    if (this.destruido) {
      this.colaActiva = false
      return
    }
    if (this.cancelando) {
      for (const item of this.cola) if (item.estado === 'espera') item.estado = 'cancelado'
    }
    const siguiente = this.cola.find(i => i.estado === 'espera')
    if (!siguiente) {
      this.cerrarCola()
      return
    }
    const renglon = this.renglones[siguiente.index]
    if (!renglon?.archivo) {
      siguiente.estado = 'cancelado'
      this.siguienteEnCola()
      return
    }
    siguiente.estado = 'subiendo'
    siguiente.error = ''
    this.subirArchivo(renglon)
    // Si por alguna guarda no salió, que la cola no se quede colgada.
    if (siguiente.estado === 'subiendo' && !this.subiendo) {
      siguiente.estado = 'cancelado'
      this.siguienteEnCola()
    }
  }

  // Cada subida de la cola termina aquí (respuesta, error o sin cuerpo).
  private trasEnvio(renglon: IRenglonArchivo) {
    if (!this.colaActiva) return
    const item = this.cola.find(i => i.index === renglon.index && i.estado === 'subiendo')
    if (item) {
      item.error = renglon.errorSubida ?? ''
      item.estado = item.error ? 'error' : 'guardado'
      this.avisoCola = item.error ? `«${renglon.etiqueta}» no se pudo subir. ` : ''
    }
    this.siguienteEnCola()
  }

  private cerrarCola() {
    this.colaActiva = false
    this.cancelando = false
    this.avisoCola = ''
    this.enfocar = {destino: 'accion', soloSiSePerdio: true}
    if (this.completoPendiente) {
      this.completoPendiente = false
      if (!this.terminado) this.cerrarPaso()
      return
    }
    // El resumen de la cola dice lo que pasó; el «¡Se guardó!» de uno sobra.
    if (!this.terminado) this.mensajeExito = ''
  }

  private enviar(renglon: IRenglonArchivo, form: FormData, alResponder: (r: IRespuestaDocumento) => void) {
    this.subiendo = true
    this.progreso = 0
    this.analizando = false
    this.mensajeExito = ''
    this.avisoValidacion = ''
    renglon.errorSubida = ''
    let respondio = false
    let cerrado = false
    // uploadDocumentConProgreso convierte casi todo error en {error: true}; si
    // aun así revienta (respuesta sin cuerpo), que los botones no se queden apagados.
    const fallar = () => {
      this.subiendo = false
      this.analizando = false
      renglon.errorSubida = 'Hubo un error de conexión'
      // Igual que un error con respuesta: en multiarchivo la lista se recarga
      // por detrás por si el backend alcanzó a guardar algo.
      if (renglon !== this.unico) this.onUpdateStage.emit()
    }
    // La cola sigue solo cuando esta petición ya cerró: nunca dos a la vez.
    const cerrar = () => {
      if (cerrado) return
      cerrado = true
      this.trasEnvio(renglon)
    }
    // Si sale de la pantalla a media subida, la petición no se aborta (el backend
    // puede estar ya analizando el archivo) pero su respuesta se ignora: la cola
    // no sigue, no se emite nada y no se crean vistas previas.
    this.flowDataService.uploadDocumentConProgreso(form).subscribe({
      next: evento => {
        if (this.destruido) return
        if (evento.tipo === 'progreso') {
          this.progreso = Math.max(this.progreso, Math.min(100, Math.round(evento.porcentaje)))
          // El archivo ya llegó: falta que el backend lo analice (hasta ~50 s).
          this.analizando = this.progreso >= 100
          return
        }
        respondio = true
        this.subiendo = false
        this.analizando = false
        this.progreso = 100
        alResponder(evento.respuesta)
      },
      error: () => {
        if (this.destruido) return
        fallar()
        cerrar()
      },
      complete: () => {
        if (this.destruido) return
        if (!respondio) fallar()
        cerrar()
      }
    })
  }

  private alResponderMultiarchivo(renglon: IRenglonArchivo, r: IRespuestaDocumento) {
    const etapa = (r.data ?? null) as EtapaRespuesta
    if (r.error) {
      // Si el análisis de un archivo ya cargado falla, el backend quita ese
      // índice: si manda el FlowData se refleja; el archivo elegido se conserva
      // para reintentar.
      renglon.errorSubida = r.msg || 'No pudimos subir tu archivo. Intenta de nuevo.'
      if (Array.isArray(etapa?.files)) {
        this.aplicarArchivos(etapa!.files!)
        this.recordarGuardados(etapa)
      }
      if (this.completoPendiente) this.completoPendiente = this.pasoCompleto(etapa)
      if (this.completoMinimo) this.completoMinimo = this.pasoCompleto(etapa)
      this.onUpdateStage.emit()
      return
    }
    this.registrarSubida(renglon)
    if (etapa?._id) this.idEtapa = String(etapa._id)
    if (Array.isArray(etapa?.files)) this.aplicarArchivos(etapa!.files!)
    else this.marcarRenglon(renglon, {status: 'VALIDATED'})
    this.recordarGuardados(etapa)
    this.mensajeExito = r.msg
    if (this.pasoCompleto(etapa)) this.completar(renglon.index)
    else {
      this.completoPendiente = false
      this.completoMinimo = false
      this.avanzarDesde(renglon.index)
      this.onUpdateStage.emit()
    }
  }

  // Documento de identidad: lo que respondió el servidor ya es de la elegida (con
  // la primera subida descartó los de la anterior). Se recuerda para volver a
  // verlo si cambia y regresa a ella.
  private recordarGuardados(etapa: EtapaRespuesta) {
    if (!this.esIdentidad || !this.identificacion) return
    this.identificacionGuardada = this.aceptada(etapa?.identificacion) ?? this.identificacion
    this.archivosGuardados = this.renglones
      .filter(r => r.estado !== 'PENDIENTE')
      .map(r => ({
        index: r.index,
        status: r.estado === 'RECHAZADO' ? 'REJECTED' : 'VALIDATED',
        comment: r.comentario,
        url: r.url,
        uploadedDate: r.fechaSubida ?? undefined
      }))
  }

  // Paso completo: la celebración de siempre, salvo que la cola aún tenga
  // archivos por subir (reemplazos u opcionales): entonces al vaciarla.
  private completar(indice = this.indiceActivo) {
    const quedan = this.colaActiva && !this.cancelando && this.cola.some(i => i.estado === 'espera')
    if (quedan) {
      this.completoPendiente = true
      return
    }
    this.cerrarPaso(indice)
  }

  // Con todo cargado (o sin rango), la celebración y el regreso de siempre. Con
  // rango y opcionales por subir, se queda en la pantalla: aviso de «ya tienes
  // los necesarios» con «Continuar», la zona en el siguiente opcional y la lista
  // se recarga por detrás (el paso ya está Listo).
  private cerrarPaso(indice = this.indiceActivo) {
    if (!this.quedanOpcionales) {
      this.completoMinimo = false
      this.terminar()
      return
    }
    this.completoMinimo = true
    this.edicion = null
    this.avanzarDesde(indice)
    this.onUpdateStage.emit()
  }

  // Lo elegido pasa a «subido» (con su miniatura) y deja libre la zona de carga.
  private registrarSubida(renglon: IRenglonArchivo) {
    const archivo = renglon.archivo
    if (!archivo) return
    this.revocar(renglon.subido?.miniatura)
    renglon.subido = {nombre: archivo.name, tamano: archivo.size, tipo: tipoDeArchivo(archivo), miniatura: renglon.vistaPrevia ?? null}
    renglon.vistaPrevia = null
    renglon.archivo = null
    renglon.errorSubida = ''
    renglon.fechaSubida = new Date()
    renglon.estado = 'CARGADO'
  }

  // Tras subir uno, la zona apunta sola al siguiente que falte.
  private avanzarDesde(indice: number) {
    const total = this.renglones.length
    for (let paso = 1; paso <= total; paso++) {
      const siguiente = this.renglones[(indice + paso) % total]
      if (siguiente.estado !== 'CARGADO') {
        this.indiceActivo = siguiente.index
        return
      }
    }
  }

  private terminar() {
    this.terminado = true
    this.arrastrando = false
    this.edicion = null
    if (this.destruido) return
    this.cancelarRegreso()
    this.temporizadorRegreso = setTimeout(() => this.emitirRegreso(), ESPERA_REGRESO)
  }

  private emitirRegreso() {
    this.temporizadorRegreso = null
    if (this.regresoEmitido) return
    this.regresoEmitido = true
    this.onEndProcess.emit(true)
  }

  private cancelarRegreso() {
    if (this.temporizadorRegreso) clearTimeout(this.temporizadorRegreso)
    this.temporizadorRegreso = null
  }

  private armarRenglones() {
    this.reiniciarCarga()
    this.idEtapa = this.data?.userDocumentId ?? ''
    this.unico = renglonVacio(0, this.titulo)
    if (this.data?.status === 'REJECTED') {
      this.unico.estado = 'RECHAZADO'
      this.unico.comentario = this.data.comment ?? ''
    } else if (this.data?.value) this.unico.estado = 'CARGADO'
    this.unico.url = this.data?.value ?? ''
    if (!this.esMultiarchivo) {
      this.renglones = []
      this.indiceActivo = 0
      return
    }
    this.armarFilas()
    // Se volvió a abrir un paso con rango que ya pasó con el mínimo: los
    // opcionales siguen abiertos, con el aviso y «Continuar».
    this.completoMinimo = this.data.status === 'VALIDATED' && this.quedanOpcionales && this.completoLocal && !this.identificacionCambiada
  }

  // Lo de una subida anterior (cola, avisos, celebración, vistas previas) ya no aplica.
  private reiniciarCarga() {
    this.cancelarRegreso()
    this.revocarTodas()
    this.terminado = false
    this.completoMinimo = false
    this.regresoEmitido = false
    this.avisoValidacion = ''
    this.mensajeExito = ''
    this.cola = []
    this.colaActiva = false
    this.cancelando = false
    this.completoPendiente = false
    this.edicion = null
    this.archivosDescartados = []
    this.avisoSobrantes = null
  }

  // Un renglón por archivo que pide el paso (o la identificación elegida), con lo
  // que ya tiene el servidor; al abrir, la primera que falte (pendiente o rechazada).
  private armarFilas() {
    const cantidad = cantidadArchivos(this.archivosPaso)
    // Una INE de un archivo sin etiqueta: «INE», no «Archivo 1».
    const nombre = this.esIdentidad && this.identificacion && cantidad === 1 && !(this.archivosPaso.fileLabels?.[0] ?? '').trim()
      ? NOMBRE_IDENTIFICACION[this.identificacion]
      : ''
    this.renglones = Array.from({length: cantidad}, (_, index) => renglonVacio(index, nombre || etiquetaArchivo(this.archivosPaso, index)))
    this.aplicarArchivos(this.archivosDeLaElegida())
    const primera = this.renglones.find(r => r.estado !== 'CARGADO')
    this.indiceActivo = primera ? primera.index : 0
  }

  // Lo que diga el backend manda: un índice sin archivo vuelve a pendiente (p. ej.
  // si el análisis de ese archivo falló y lo quitó).
  private aplicarArchivos(archivos: IFlowFile[]) {
    for (const renglon of this.renglones) {
      const archivo = archivos.find(a => a && Number(a.index) === renglon.index)
      if (archivo) this.marcarRenglon(renglon, archivo)
      else {
        renglon.estado = 'PENDIENTE'
        renglon.comentario = ''
        renglon.fechaSubida = null
        renglon.url = ''
        this.revocar(renglon.subido?.miniatura)
        renglon.subido = null
      }
    }
  }

  private marcarRenglon(renglon: IRenglonArchivo, archivo: Pick<IFlowFile, 'status'> & Partial<IFlowFile>) {
    renglon.estado = archivo.status === 'REJECTED' ? 'RECHAZADO' : 'CARGADO'
    renglon.comentario = archivo.comment ?? ''
    if (archivo.uploadedDate) renglon.fechaSubida = archivo.uploadedDate
    if (archivo.url) renglon.url = archivo.url
  }

  // Lo decide el backend: manda `completed` y deja el FlowData en IN_PROGRESS
  // mientras falte algún archivo (o no llegue al mínimo, con rango) o haya uno
  // rechazado. Sin ninguno de los dos, se decide con los renglones con la misma
  // regla: el mínimo cargado y ninguno rechazado.
  private pasoCompleto(etapa: EtapaRespuesta): boolean {
    if (typeof etapa?.completed === 'boolean') return etapa.completed
    if (etapa?.status) return !['IN_PROGRESS', 'REJECTED'].includes(etapa.status)
    return this.completoLocal
  }

  // ---------- Varios a la vez: apoyo ----------

  // A dónde va cada archivo de una selección múltiple: desde la activa (aunque
  // ya esté cargada: se eligió para reemplazarla) y luego, dando la vuelta, las
  // que faltan; nunca una que ya tiene archivo elegido.
  private destinosLibres(): IRenglonArchivo[] {
    const total = this.renglones.length
    const destinos: IRenglonArchivo[] = []
    for (let paso = 0; paso < total; paso++) {
      const renglon = this.renglones[(this.indiceActivo + paso) % total]
      if (!renglon || renglon.archivo) continue
      if (renglon.estado !== 'CARGADO' || paso === 0) destinos.push(renglon)
    }
    return destinos
  }

  private asignar(renglon: IRenglonArchivo, archivo: File) {
    this.revocar(renglon.vistaPrevia)
    renglon.archivo = archivo
    renglon.errorSubida = ''
    renglon.vistaPrevia = this.crearVistaPrevia(archivo)
  }

  // Cambió la selección: el resumen de la cola anterior y sus avisos ya no aplican.
  private olvidarResumen() {
    if (this.colaActiva) return
    this.cola = []
    this.archivosDescartados = []
    this.avisoSobrantes = null
  }

  private motivoDescarte(archivo: File): string {
    if (!this.formatoValido(archivo)) {
      return this.reglasCarga.extensiones.length === 3 ? 'no es una foto (JPG o PNG) ni un PDF' : `no es ${this.formatos}`
    }
    if (archivo.size > TAMANO_MAXIMO_DOCUMENTO) return `pesa más de ${TAMANO_MAXIMO_DOCUMENTO_TEXTO}`
    return ''
  }

  // «Elegiste 8; este paso pide 6: usamos los primeros 6».
  private textoSobrantes(elegidos: number, faltan: number, sobrantes: File[]): {titulo: string; texto: string} {
    const fuera = `Quedaron fuera: ${unirNombres(sobrantes.map(a => `«${a.name}»`))}.`
    if (!faltan) return {titulo: `Ya tienes un archivo para cada ${this.sustantivo} que falta`, texto: `${fuera} Para cambiar uno, toca su tarjeta.`}
    const total = this.renglones.length
    const pide = faltan >= total ? `este paso pide ${total}` : `te ${faltan === 1 ? 'falta' : 'faltan'} ${faltan}`
    const usamos = faltan === 1 ? 'usamos el primero' : `usamos los primeros ${faltan}`
    return {titulo: `Elegiste ${elegidos}; ${pide}: ${usamos}`, texto: fuera}
  }

  private armarFilasLote(): IFilaLote[] {
    if (!this.esMultiarchivo) return []
    if (this.cola.length) {
      return this.cola.map(item => {
        const renglon = this.renglones[item.index]
        return {
          index: item.index,
          etiqueta: renglon?.etiqueta ?? '',
          nombre: renglon?.archivo?.name ?? item.nombre,
          tamano: renglon?.archivo?.size ?? item.tamano,
          tipo: renglon?.archivo ? tipoDeArchivo(renglon.archivo) : item.tipo,
          miniatura: renglon ? this.miniaturaTarjeta(renglon) : null,
          estado: item.estado,
          error: item.error
        }
      })
    }
    return this.renglones.filter(r => !!r.archivo).map(r => ({
      index: r.index,
      etiqueta: r.etiqueta,
      nombre: r.archivo!.name,
      tamano: r.archivo!.size,
      tipo: tipoDeArchivo(r.archivo),
      miniatura: r.vistaPrevia ?? null,
      estado: this.subiendo && r.index === this.indiceActivo ? 'subiendo' : r.errorSubida ? 'error' : 'listo',
      error: r.errorSubida ?? ''
    }))
  }

  private get itemEnCurso(): IItemCola | undefined {
    return this.cola.find(i => i.estado === 'subiendo')
  }

  private etiquetaDe(indice: number): string {
    return this.renglones[indice]?.etiqueta ?? ''
  }

  // «1 recibo», «6 recibos» (o archivo/archivos).
  private contar(n: number): string {
    return `${n} ${this.sustantivo}${n === 1 ? '' : 's'}`
  }

  private focoPerdido(): boolean {
    if (typeof document === 'undefined') return false
    const activo = document.activeElement
    return !activo || activo === document.body || !this.host.nativeElement.contains(activo)
  }

  private sinMovimiento(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  // Solo se desplaza si hace falta (nearest), y sin animación si así lo pidió.
  private verSinBrinco(elemento: HTMLElement) {
    if (typeof elemento.scrollIntoView !== 'function') return
    elemento.scrollIntoView({behavior: this.sinMovimiento() ? 'auto' : 'smooth', block: 'nearest'})
  }

  private extraerArchivos(evento: any): File[] {
    const lista = evento?.files ?? evento?.target?.files ?? evento?.dataTransfer?.files
    // Se copia antes de limpiar el input: su FileList se vacía con value = ''.
    const archivos: File[] = lista ? Array.from(lista as ArrayLike<File>).filter(a => !!a) : []
    // Limpia el input para que elegir el mismo archivo otra vez vuelva a disparar change.
    const input = evento?.target
    if (typeof HTMLInputElement !== 'undefined' && input instanceof HTMLInputElement) input.value = ''
    return archivos
  }

  private extraerArchivo(evento: any): File | null {
    return this.extraerArchivos(evento)[0] ?? null
  }

  private crearVistaPrevia(archivo: File): string | null {
    if (tipoDeArchivo(archivo) !== 'imagen' || typeof URL.createObjectURL !== 'function') return null
    try {
      const url = URL.createObjectURL(archivo)
      this.urls.add(url)
      return url
    } catch {
      return null
    }
  }

  private revocar(url: string | null | undefined) {
    if (!url) return
    try {
      URL.revokeObjectURL(url)
    } catch {
      // Nada que liberar.
    }
    this.urls.delete(url)
  }

  private revocarTodas() {
    for (const url of Array.from(this.urls)) this.revocar(url)
  }
}
